import { useEffect, useRef } from "react";
import { useUser } from "./useUser";
import { type ThemePref, useThemeStore } from "./useTheme";

const STORAGE_KEY = "pmt-theme";

// useThemeSync hydrates the theme store from the server's
// User.theme_preference once /me resolves. Wired in App.tsx so it's
// always mounted while a user is signed in.
//
// Per-uid hydration tracking (not per-mount): App.tsx stays mounted
// across the /login bounce, so a plain boolean ref would skip
// re-hydration if the user signs out and signs in as a different
// user. We track the uid we last hydrated against and re-hydrate
// when it changes.
//
// Persists to localStorage too (despite setPrefSilent itself skipping
// the write to avoid the cross-tab cascade): hydration is one-shot,
// and writing here means the no-flash bootstrap on the NEXT cold load
// reads the correct value. E.g. user toggled "light" on phone; signs
// in on desktop whose localStorage still says "dark" from a prior
// session — without this write, the next cold load on desktop would
// flash dark before hydration corrects it.
export function useThemeSync() {
  const { data: user } = useUser();
  const setPrefSilent = useThemeStore((s) => s.setPrefSilent);
  const hydratedUid = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return;
    if (hydratedUid.current === user.id) return;
    hydratedUid.current = user.id;
    if (user.theme_preference) {
      const next = user.theme_preference as ThemePref;
      setPrefSilent(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
    }
  }, [user, setPrefSilent]);
}
